import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { UNROUTED_REASON_KEYS } from "../unroutedDevices";
import type { CablePosition } from "../findCableGaps";
import { PanelSection } from "../PanelSection";
import { useSectionDisclosure } from "../useSectionDisclosure";
import { UnroutedDeviceRow } from "./UnroutedDeviceRow";
import type { UnroutedDevicesNoteProps } from "./types";

const positionsOf = (
  devices: { position: CablePosition | null }[],
): CablePosition[] =>
  devices.flatMap((device) => (device.position ? [device.position] : []));

export const UnroutedDevicesNote = ({
  summary,
  onSpotlight,
  circuitOptions,
  onAdopt,
  onOpenAssignMode,
  isOpen,
  onOpenChange,
}: UnroutedDevicesNoteProps) => {
  const { t } = useTranslation();

  // Held in a ref: the panel rebuilds this callback on every edit, and the
  // unmount cleanup below must not re-run for a new arrow.
  const spotlightRef = useRef(onSpotlight);
  spotlightRef.current = onSpotlight;
  // Never true on the first render, so mounting closed cannot take a spotlight
  // somebody else set.
  const wasOpenRef = useRef(false);

  const disclosure = useSectionDisclosure({ isOpen, onOpenChange });
  const [openKind, setOpenKind] = useState<string | null>(null);
  const [pinnedDevice, setPinnedDevice] = useState<string | null>(null);

  const kinds = useMemo(() => summary?.byKind ?? [], [summary]);

  const targets = useMemo(
    () =>
      (circuitOptions ?? []).map((option) => ({
        value: option.id,
        label: option.label,
        color: option.color,
      })),
    [circuitOptions],
  );

  const toggleKind = useCallback(
    (name: string) => {
      const next = openKind === name ? null : name;
      setOpenKind(next);
      setPinnedDevice(null);
      const kind = kinds.find((entry) => entry.name === name);
      spotlightRef.current?.(
        next && kind ? positionsOf(kind.devices) : null,
      );
    },
    [kinds, openKind],
  );

  const pinDevice = useCallback(
    (id: string, position: CablePosition | null, kindName: string) => {
      // Unpinning falls back to the whole kind rather than clearing the plan:
      // the list he is reading is still open, and its marks belong under it.
      const next = pinnedDevice === id ? null : id;
      setPinnedDevice(next);
      const kind = kinds.find((entry) => entry.name === kindName);
      spotlightRef.current?.(
        next && position ? [position] : kind ? positionsOf(kind.devices) : null,
      );
    },
    [kinds, pinnedDevice],
  );

  // Closing hands the plan back, whichever way it closed: from its own header,
  // or because he opened another section and the panel closed this one. A kind
  // left lit under a closed section is a highlight with no cause on screen.
  useEffect(() => {
    if (disclosure.isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    setOpenKind(null);
    setPinnedDevice(null);
    spotlightRef.current?.(null);
  }, [disclosure.isOpen]);

  useEffect(() => () => spotlightRef.current?.(null), []);

  if (!summary?.total) return null;

  const missingScheduleCount =
    summary.byReason.find(
      ({ reason }) => reason === "no_circuit_id_reaches_it",
    )?.count ?? 0;

  const open = kinds.find((kind) => kind.name === openKind) ?? null;

  return (
    <PanelSection
      tone="unrouted"
      title={t("smartSurveyor.cablesBeta.unroutedTitle")}
      count={summary.total}
      isOpen={disclosure.isOpen}
      onToggle={disclosure.toggle}
    >
      <p className="text-[13px] leading-snug text-slate-600">
        {t("smartSurveyor.cablesBeta.unroutedNotCounted")}
      </p>
      {onOpenAssignMode && (
        <button
          type="button"
          onClick={onOpenAssignMode}
          className="mt-1.5 w-full rounded-md bg-blue-600 px-3 py-2 text-[13.5px] font-semibold text-white hover:opacity-90"
        >
          {t("smartSurveyor.cablesBeta.unroutedOpenAssignMode")}
        </button>
      )}
      {/* WHAT they are, as the symbols he reads off the drawing. A click lights
          the whole kind on the plan AND opens it as rows — a count and a
          highlight is not something he can work through. */}
      {kinds.length > 0 && (
        <ul data-testid="unrouted-kinds" className="mt-1.5 flex flex-wrap gap-1">
          {kinds.map((kind) => {
            const isKindOpen = openKind === kind.name;
            return (
              <li key={kind.name}>
                <button
                  type="button"
                  aria-pressed={isKindOpen}
                  aria-label={t("smartSurveyor.cablesBeta.unroutedKindOpen", {
                    count: kind.count,
                    name: kind.name,
                  })}
                  onClick={() => toggleKind(kind.name)}
                  className={clsx(
                    "flex items-center gap-1.5 rounded-md border py-1 pl-1 pr-2 text-[13px] transition-colors",
                    isKindOpen
                      ? "border-violet-500 bg-violet-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-violet-400",
                  )}
                >
                  {kind.iconDataUrl ? (
                    <img
                      src={kind.iconDataUrl}
                      alt=""
                      draggable={false}
                      className="h-8 w-8 flex-none rounded border border-slate-200 object-cover"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="grid h-8 w-8 flex-none place-items-center rounded border border-slate-200 bg-slate-50"
                    >
                      <span className="h-3 w-3 border-2 border-violet-400" />
                    </span>
                  )}
                  <span className="font-semibold tabular-nums">{kind.count}</span>
                  <span className="max-w-[10rem] truncate">{kind.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {open && (
        <div
          data-testid="unrouted-kind-list"
          className="mt-1.5 rounded-md border border-slate-200 px-1 py-0.5"
        >
          {open.devices.map((device) => (
            <UnroutedDeviceRow
              key={device.id}
              device={device}
              iconDataUrl={open.iconDataUrl}
              isPinned={pinnedDevice === device.id}
              onPin={() => pinDevice(device.id, device.position, open.name)}
              targets={targets}
              onAdopt={
                onAdopt && device.position
                  ? (circuitId) =>
                      onAdopt(
                        device.id,
                        circuitId,
                        device.position as CablePosition,
                        device.name,
                      )
                  : undefined
              }
            />
          ))}
        </div>
      )}
      {summary.hasMissingSchedule && (
        <p className="mt-1.5 text-[13px] font-medium leading-snug text-slate-800">
          {t("smartSurveyor.cablesBeta.unroutedAskForSchedule", {
            count: missingScheduleCount,
          })}
        </p>
      )}
      <ul data-testid="unrouted-reasons" className="mt-1.5 space-y-0.5">
        {summary.byReason.map(({ reason, count }) => (
          <li key={reason} className="text-[13px] leading-snug text-slate-600">
            {t("smartSurveyor.cablesBeta.unroutedReasonCount", {
              count,
              reason: t(UNROUTED_REASON_KEYS[reason]),
            })}
          </li>
        ))}
      </ul>
    </PanelSection>
  );
};
